import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Loader2, ArrowRight, Lock, Play, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { api, extractError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../context/AuthContext";

const STATUS_BADGE = {
    draft: { label: "Draft", className: "bg-slate-100 text-slate-700 border-slate-200" },
    in_progress: { label: "In progress", className: "bg-amber-50 text-amber-800 border-amber-200" },
    locked: { label: "Locked", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export default function CyclesListPage() {
    const { user } = useAuth();
    const [cycles, setCycles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const canCreate = user?.role === "platform_admin" || user?.role === "org_leader";

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/cycles");
            setCycles(data);
            setError(null);
        } catch (e) {
            setError(extractError(e, "Could not load cycles"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    return (
        <div className="space-y-6" data-testid="cycles-list-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.25em] font-bold text-[#1E56A0] mb-2">
                        Assessment cycles
                    </div>
                    <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-[#0B2545] leading-tight">
                        Coherence audits
                    </h1>
                    <p className="mt-2 text-sm text-[#0E1B2C]/70 max-w-xl">
                        Each cycle scores your organization across the six ESSS domains. Phase 2 rubrics
                        are active for Vision, Curriculum &amp; Assessment, and Instructional Core.
                    </p>
                </div>
                {canCreate && (
                    <Button
                        className="rounded-full bg-[#0B2545] hover:bg-[#1E56A0] text-white h-10 px-5"
                        onClick={() => navigate("/app/cycles/new")}
                        data-testid="new-cycle-btn"
                    >
                        <Plus size={16} /> New cycle
                    </Button>
                )}
            </div>

            {loading ? (
                <div className="text-[#0B2545] flex items-center gap-3">
                    <Loader2 className="animate-spin" size={18} /> Loading cycles…
                </div>
            ) : error ? (
                <div className="text-red-700">{error}</div>
            ) : cycles.length === 0 ? (
                <div
                    className="rounded-2xl border border-dashed border-[#8AC4FF] bg-white p-10 text-center"
                    data-testid="cycles-empty"
                >
                    <ClipboardList size={28} className="mx-auto text-[#1E56A0]" />
                    <p className="mt-3 text-[#0B2545] font-semibold">No cycles yet.</p>
                    <p className="text-sm text-[#0E1B2C]/70">
                        {canCreate ? "Create your first cycle to begin scoring." : "Ask an admin to start a cycle."}
                    </p>
                </div>
            ) : (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="cycles-list">
                    {cycles.map((c) => {
                        const b = STATUS_BADGE[c.status] || STATUS_BADGE.draft;
                        return (
                            <li key={c.id}>
                                <Link
                                    to={`/app/cycles/${c.id}`}
                                    className="block rounded-xl bg-white border border-[#D6E6F2] p-5 hover:border-[#8AC4FF] hover:shadow-lg hover:shadow-[#0B2545]/5 transition-all"
                                    data-testid={`cycle-card-${c.id}`}
                                >
                                    <div className="flex items-center justify-between mb-2">
                                        <Badge className={b.className + " border"} variant="outline">
                                            {c.status === "locked" ? (
                                                <Lock size={12} className="mr-1" />
                                            ) : c.status === "in_progress" ? (
                                                <Play size={12} className="mr-1" />
                                            ) : null}
                                            {b.label}
                                        </Badge>
                                        <ArrowRight className="text-[#1E56A0]" size={18} />
                                    </div>
                                    <div className="font-serif text-xl text-[#0B2545] font-semibold leading-snug">
                                        {c.name}
                                    </div>
                                    <div className="mt-2 text-xs text-[#0E1B2C]/60">
                                        Created {new Date(c.created_at).toLocaleDateString()}
                                    </div>
                                </Link>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
